import { useCallback, useMemo, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormDivider from "@components/FormDivider/FormDivider";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import StepperControls from "@components/StepperControls/StepperControls.tsx";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { usePersistentForm } from "@hooks/usePersistentForm";
import PageLayout from "@layouts/PageLayout";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useSubmitGatepassRequest } from "../hooks/useSubmitGatepassRequest";
import {
  getNormalGatepassFormSchema,
  type NormalGatepassFormData,
} from "../schema/normalGatepassFormSchema";
import {
  getOffshoreGatepassFormSchema,
  type OffshoreGatepassFormData,
} from "../schema/offshoreGatepassFormSchema";
import {
  getTruckGatepassFormSchema,
  type TruckGatepassFormData,
} from "../schema/truckGatepassFormSchema";
import { hasHazardCargoWithMultipleTypes } from "./TruckGatepass/truckEntryDateSync";
import { GATEPASS_TYPES, gatepassStoresByType, getGatepassCategoryTranslationKey } from "./utils";

const LANG_BASE = "gatepass";
const INDIVIDUAL_ROLES = ["security-applicant", "uae-pass-user"];
const OFFSHORE_TYPE = "offshore";
const TRUCK_TYPE = "truck";

const getPersistKeyByType = (isOffshore: boolean, isTruck: boolean) =>
  isOffshore ? "offshore-gatepass-form" : isTruck ? "truck-gatepass-form" : "normal-gatepass-form";

const GatepassType = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { type } = useParams();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitPending, setSubmitPending] = useState(false);

  const isDocumentUploadPending = useUploadStore((state) =>
    Object.values(state.uploadsInFlightByKey).some((count) => count > 0),
  );

  const useGatepassStore = type && gatepassStoresByType[type];
  const isOffshore = type === OFFSHORE_TYPE;
  const isTruck = type === TRUCK_TYPE;

  const setField = useGatepassStore(
    (state: { setField: (key: string, value: unknown) => void }) => state.setField,
  );
  const setAll = useGatepassStore(
    (state: { setAll: (data: Record<string, unknown>) => void }) => state.setAll,
  );
  const resetForm = useGatepassStore((state: { resetForm: () => void }) => state.resetForm);
  const securityClearanceId = useGatepassStore(
    (state: { securityClearanceId?: string }) => state.securityClearanceId ?? "",
  );
  const { user } = useAuthStore();
  const isIndividualRole = INDIVIDUAL_ROLES.some((role) => user?.roles?.includes(role));

  const dialogProps = {
    testId: "normal-gatepass-cancel-dialog",
    title: t(`${LANG_BASE}.cancel-dialog-title`),
    cancelButtonText: t(`common.no`),
    confirmButtonText: t(`common.yes`),
    handleCloseDialog: () => setDialogOpen(false),
    confirmAction: () => {
      clearForm();
      setDialogOpen(false);
    },
    elementContent: (
      <div className="text-left">
        <p>{t(`${LANG_BASE}.cancel-dialog-description`)}</p>
      </div>
    ),
  };

  const clearForm = () => {
    resetForm();
    reset();
    localStorage.removeItem(getPersistKeyByType(isOffshore, isTruck));
    useUploadStore.getState().clearFilesForKey("photoUrl");
    useUploadStore.getState().clearFilesForKey("deliveryNotesDocuments");
    navigate("/gatepass");
  };

  const form = usePersistentForm<
    NormalGatepassFormData | OffshoreGatepassFormData | TruckGatepassFormData
  >({
    schema: isOffshore
      ? getOffshoreGatepassFormSchema(t)
      : isTruck
        ? getTruckGatepassFormSchema(t, { isIndividualRole })
        : getNormalGatepassFormSchema(t, { isIndividualRole }),
    defaultValues: {
      ...useGatepassStore?.getState(),
    },
    storeValues: {
      ...useGatepassStore?.getState(),
    },
    setStoreValues: setAll,
    resetStore: clearForm,
    persistKey: getPersistKeyByType(isOffshore, isTruck),
  });

  const { reset, getValues } = form;

  const updateFormData = useCallback(
    (key: string, value: unknown) => {
      setField(key, value);
    },
    [setField],
  );

  const outletContext = useMemo(() => ({ formData: form, updateFormData }), [form, updateFormData]);

  const { onSubmit } = useSubmitGatepassRequest({
    getFormValues: () => ({
      ...useGatepassStore.getState(),
      ...form.getValues(),
      gatepassCategory: isOffshore ? "OFFSHORE" : isTruck ? "TRUCK" : "NORMAL",
    }),
    resetForm: clearForm,
    securityClearanceId,
    onSubmitError: () => setSubmitPending(false),
  });

  const onValid = async () => {
    if (useUploadStore.getState().hasAnyUploadInFlight()) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    setSubmitPending(true);
    await onSubmit();
  };

  const notifyValidationError = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.gatepass-request-submitted-error-title`),
      description: t(`${LANG_BASE}.notifications.gatepass-request-submitted-error-description`),
      type: "error",
    });
  };

  const notifyHazardMultipleCargoError = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.gatepass-request-submitted-error-title`),
      description: t(`${LANG_BASE}.hazard-cargo-multiple-types-not-allowed`),
      type: "error",
    });
  };

  const onInvalid = () => {
    notifyValidationError();
  };

  const handleSubmit = () => {
    if (isTruck) {
      const truckValues = getValues() as TruckGatepassFormData;
      if (hasHazardCargoWithMultipleTypes(truckValues.cargos)) {
        notifyHazardMultipleCargoError();
        form.handleSubmit(onValid, () => undefined)();
        return;
      }
    }

    form.handleSubmit(onValid, onInvalid)();
  };

  const handleComplete = () => {
    if (submitPending) {
      return;
    }
    if (isDocumentUploadPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    handleSubmit();
  };

  const cancel = async () => {
    setDialogOpen(true);
  };

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId="gatepass-request-container-subheader"
        subtitle={t(`${LANG_BASE}.request-gatepass`)}
        title={t(`${LANG_BASE}.request-gatepass-type`, {
          type: type
            ? type === GATEPASS_TYPES.TRUCK
              ? t(`${LANG_BASE}.truck-cargo`)
              : t(getGatepassCategoryTranslationKey(type))
            : "",
        })}
      />
      <div className="max-w-[824px] mx-auto">
        <div className="my-8">
          <Outlet context={outletContext} />
        </div>
        {!isOffshore && <FormDivider />}
        <div className="py-8">
          <StepperControls
            cancel={cancel}
            complete={handleComplete}
            completePending={submitPending}
            completeLabelTranslationKey="stepper-controls.submit"
          />
        </div>
      </div>
      <GenericDialog open={dialogOpen} {...dialogProps} />
    </PageLayout>
  );
};

export default GatepassType;
